import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import APIS from "../../constant/Endpoint";
import useGetHook from "../../customHooks/useGetHook";
import usePostHook from "../../customHooks/usePostHook";
import { NOTIFICATION_OFFSET, NOTIFICATION_TYPE, UNREAD } from "./constants";
import { INotificationContext, TNotification } from "./types";

export const NotificationContext = createContext<INotificationContext>({
  notifications: [],
  noticeList: [],
  unreadCount: 0,
  noticeCount: 0,
  active: "notification",
  isRefetching: false,
  currentCount: 0,
  filteredNotification: [],
  filteredNoticeList: [],
  searchTerm: "",
});

export default function NotificationContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<TNotification[]>([]);
  const [noticeList, setNoticeList] = useState<TNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [noticeCount, setNoticeCount] = useState<number>(0);
  const [currentCount, setCurrentCount] = useState<number>(0);
  const [active, setActive] = useState<"notice" | "notification" | "chat">(
    "notification"
  );
  const [filteredNotification, setFilteredNotification] = useState<
    TNotification[]
  >([]);
  const [filteredNoticeList, setFilteredNoticeList] = useState([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams({
    notificationType: "unread",
    limit: "10",
    offset: "0",
    search: "",
  });
  const [hasNext, setHasNext] = useState(null);
  const [hasPrevious, setHasPrevious] = useState(null);
  const memoizedNotifications = useMemo(() => notifications, [notifications]);
  const memoizedNoticeList = useMemo(() => noticeList, [noticeList]);
  const [showPannel, setShowPannel] = useState(false);
  const [openInbox, setOpenInbox] = useState<{
    open: boolean;
    group: any;
  }>({
    open: false,
    group: {},
  });

  const notificationParams = useCallback(() => {
    return {
      deleted: false,
      unread: searchParams.get(NOTIFICATION_TYPE) === UNREAD,
      limit: "10",
      offset: searchParams.get(NOTIFICATION_OFFSET) || "0",
    };
  }, [searchParams]);

  const { data: unreadCountData, refetch } = useGetHook({
    queryKey: [`notifications-count`],
    url: `${APIS.NOTIFICATION}`,
    params: {
      unread: true,
      limit: 1,
      offset: 0,
    },
  });

  const { data: noticeListCount } = useGetHook({
    queryKey: [`notice-list-count`],
    url: `${APIS.NOTICE}`,
    params: {
      limit: 1,
      offset: 0,
    },
  });

  const {
    data: receivedNotifications,
    isLoading,
    isRefetching,
  } = useGetHook({
    enabled: showPannel,
    queryKey: [
      "notifications",
      `notification-${JSON.stringify(notificationParams())}`,
    ],
    url: `${APIS.NOTIFICATION}`,
    params: notificationParams(),
  });

  const resetNotifications = useCallback(() => {
    setNotifications([]);
    setFilteredNotification([]);
  }, []);

  const { data: noticeListData, refetch: getNotice } = useGetHook({
    enabled: showPannel,
    queryKey: [`notice-list-${active}`],
    url: `${APIS.NOTICE}`,
    params: {
      is_active: true,
    },
  });

  /**
   * used for handling read, unread and handleReadAll
   */
  const { mutateAsync } = usePostHook({
    queryKey: ["notifications", "notifications-count"],
  });

  const resetNotices = useCallback(() => {
    setNoticeList([]);
    setFilteredNoticeList([]);
  }, []);

  /**
   * Handling the changing of tab from notification to notice
   */
  const handleTabChange = useCallback(
    (active: "notification" | "notice" | "chat") => {
      resetNotices();
      setActive(active);
    },
    [resetNotifications, resetNotices]
  );

  const handleReadAll = useCallback(async () => {
    const url = `${APIS.NOTIFICATION}read-all/`;
    let formData = {};
    try {
      await mutateAsync({ url, formData });
      resetNotifications();
      refetch();
    } catch (err) {
      if (err instanceof Error) toast.error(err.message);
    }
  }, [mutateAsync, resetNotifications, refetch]);

  const handleRead = useCallback(
    async (data: Record<string, any>) => {
      const url = `${APIS.NOTIFICATION}${data?.unread ? "read" : "unread"}/`;
      const formData = {
        ids: [data?.id],
      };
      try {
        const res = await mutateAsync({ url, formData });
        if (res?.status === 200) {
          resetNotifications();
          refetch();
          const filter = notifications.filter((n) => n.id !== data.id);
          setFilteredNotification(filter);
          setNotifications((prev) => prev.filter((n) => n.id !== data.id));
        }
      } catch (err) {
        if (err instanceof Error) toast.error(err.message);
      }
    },
    [notifications, refetch, mutateAsync]
  );

  const handleNavigate = useCallback(
    async (data: Record<string, any>) => {
      const url = `${APIS.NOTIFICATION}${data?.unread ? "read" : "unread"}/`;
      const formData = {
        ids: [data?.id],
      };
      try {
        const res = await mutateAsync({ url, formData });
        if (res?.status === 200) {
          resetNotifications();
          refetch();
          const filter = notifications.filter((n) => n.id !== data.id);
          setFilteredNotification(filter);
          setNotifications((prev) => prev.filter((n) => n.id !== data.id));
          // setShowPannel((prev) => !prev);
          switch (data?.target_content_type) {
            case "Pickup Request":
              if (window.location.pathname === "/pickup-request") {
                navigate(`/pickup-request#/edit/${data?.target_object_id}`);
              }
              window.location.replace(
                `/pickup-request#/edit/${data?.target_object_id}`
              );
              break;
            case "Ticket":
              window.location.replace(`/support/ticket#/`);
              break;
            case "Remarks":
              window.location.replace(
                `/order#/?selectedItem=${data?.target_object_id}`
              );
              break;
            default:
              break;
          }
        }
      } catch (err) {
        if (err instanceof Error) toast.error(err.message);
      }
    },
    [notifications, refetch, mutateAsync]
  );

  const handleSearch = (value: string) => {
    if (active === "notification") {
      const filtered = memoizedNotifications?.filter(
        (n: TNotification) =>
          n.title.toLowerCase().includes(value.toLowerCase()) ||
          n.description.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredNotification(filtered);
    } else if (active === "notice") {
      const filtered = memoizedNoticeList?.filter((n: TNotification) =>
        n.title.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredNoticeList(filtered);
    }
    setSearchTerm(value);
  };

  useEffect(() => {
    if (unreadCountData?.count && unreadCount != unreadCountData?.count) {
      setUnreadCount(unreadCountData.count);
    }
  }, [unreadCount, unreadCountData]);

  /**
   * Set notice count
   */
  useEffect(() => {
    if (noticeListCount?.count && noticeCount != noticeListCount?.count) {
      setNoticeCount(noticeListCount.count);
    }
  }, [noticeListCount, noticeCount]);

  useEffect(() => {
    if (Array.isArray(receivedNotifications?.results) && showPannel) {
      setNotifications((prev) => [...prev, ...receivedNotifications.results]);
      setFilteredNotification((prev) => [
        ...prev,
        ...receivedNotifications.results,
      ]);
      setHasNext(!!receivedNotifications.next);
      setHasPrevious(!!receivedNotifications.previous);
    }
  }, [receivedNotifications, showPannel]);

  useEffect(() => {
    if (searchTerm) {
      handleSearch(searchTerm);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (Array.isArray(noticeListData)) {
      setNoticeList((prev) => [...prev, ...noticeListData]);
      setFilteredNoticeList((prev) => [...prev, ...noticeListData]);
    }
  }, [noticeListData]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        filteredNotification,
        noticeList,
        noticeCount,
        unreadCount,
        active,
        hasNext,
        showPannel,
        hasPrevious,
        currentCount,
        openInbox,
        setOpenInbox,
        filteredNoticeList,
        searchTerm,
        isRefetching: isLoading || isRefetching,
        handleTabChange,
        handleNavigate,
        handleRead,
        handleReadAll,
        handleSearch,
        resetNotifications,
        notificationParams,
        setShowPannel,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
